
import { Component, HostListener } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthorizedUsersDialogComponent } from '@components/authorized-users-dialog/authorized-users-dialog.component';
import { ShippingCreationDto } from '@models/users/shipping-creation.dto';
import { ShippingDeliveryDto } from '@models/users/shipping-delivery.dto';
import { ShippingDto } from '@models/users/shipping.dto';
import { SubscriberDto } from '@models/users/subscriber.dto';
import { AccountService } from '@services/account.service';
import { ShippingService } from '@services/shipping.service';
import { SubscriberService } from '@services/subscriber.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
  selector: 'ebs-shipping-management',
  templateUrl: './shipping-management.component.html',
  styleUrls: ['./shipping-management.component.scss'],
})
export class ShippingManagementComponent {
  formGroup!: FormGroup;

  public search: string = '';

  public users: SubscriberDto[] = [];

  public usersMap: {
    user: SubscriberDto;
    shippingsMap: { [key: number]: ShippingDeliveryDto };
  }[] = [];

  public user?: SubscriberDto;
  public toDeliver: ShippingDeliveryDto[] = [];
  public toDeliverMap: { [key: number]: boolean } = {};

  public isValid: boolean = false;
  public hasAlreadyDelivered: boolean = false;
  public searchDone: boolean = false;

  public creating: ShippingCreationDto | undefined;

  public shippings: ShippingDto[] = [];
  public shippingsMap: { [key: number]: ShippingDto } = {};
  public shippingDeliveries: ShippingDeliveryDto[] = [];

  public selectedShippingId: number | undefined = undefined;

  /**
   * Permesso per creare una nuova spedizione.
   * Funzionalità nel database: createShipping
   */
  public get hasCreatePermission(): boolean {
    return (
      this.accountService.group?.authorizations?.some(
        (elem) => elem.functionality.name === 'createShipping',
      ) ?? false
    );
  }

  /**
   * Permesso per eliminare una spedizione.
   * Funzionalità nel database: removeShipping
   */
  public get hasDeletePermission(): boolean {
    return (
      this.accountService.group?.authorizations?.some(
        (elem) => elem.functionality.name === 'removeShipping',
      ) ?? false
    );
  }

  /**
   * Permesso per effettuare una consegna.
   * Funzionalità nel database: deliverShipping
   */
  public get hasDeliveredPermission(): boolean {
    return (
      this.accountService.group?.authorizations?.some(
        (elem) => elem.functionality.name === 'deliverShipping',
      ) ?? false
    );
  }

  public get selectedShipping(): ShippingDto | undefined {
    if (this.selectedShippingId === undefined) {
      return undefined;
    }

    return this.shippingsMap[this.selectedShippingId];
  }

  public getShippingRecipientsCount(shippingId: number): number {
    return this.usersMap.filter(
      (entry) => entry.shippingsMap[shippingId] !== undefined,
    ).length;
  }

  public getShippingDeliveredCount(shippingId: number): number {
    return this.usersMap.filter(
      (entry) => entry.shippingsMap[shippingId]?.delivered === true,
    ).length;
  }

  public getShippingPendingCount(shippingId: number): number {
    return this.usersMap.filter((entry) => {
      const delivery = entry.shippingsMap[shippingId];

      return delivery !== undefined && !delivery.delivered;
    }).length;
  }

  public get selectedShippingUsers(): {
    user: SubscriberDto;
    shippingsMap: { [key: number]: ShippingDeliveryDto };
  }[] {
    if (this.selectedShippingId === undefined) {
      return [];
    }

    return this.usersMap.filter(
      (entry) =>
        entry.shippingsMap[this.selectedShippingId!] !== undefined,
    );
  }

  constructor(
    private subsService: SubscriberService,
    private accountService: AccountService,
    private dialogService: DialogService,
    private shippingService: ShippingService,
  ) {
    this.formGroup = new FormGroup({
      search: new FormControl('', [
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]),
    });

    this.loadAll();
  }

  loadAll() {
    this.subsService.getSubscribers().subscribe((result) => {
      this.users = result;

      this.shippingService.getShippings().subscribe((shippings) => {
        this.shippings = shippings.sort(
          (elem1: ShippingDto, elem2: ShippingDto) => {
            return elem2.shippingId! - elem1.shippingId!;
          },
        );

        this.shippingsMap = {};

        shippings.forEach((elem) => {
          this.shippingsMap[elem.shippingId!] = elem;
        });

        this.shippingService.getShippingUserList().subscribe((deliveries) => {
          this.shippingDeliveries = deliveries;
          this.usersMap = [];

          this.users.forEach((elem) => {
            const shippingsMap: {
              [key: number]: ShippingDeliveryDto;
            } = {};

            this.shippingDeliveries
              .filter((del) => del.userId === elem.subscriberId)
              .forEach((del) => {
                shippingsMap[del.shippingId] = del;
              });

            this.usersMap.push({
              user: elem,
              shippingsMap,
            });
          });

          if (
            this.selectedShippingId !== undefined &&
            this.shippingsMap[this.selectedShippingId] === undefined
          ) {
            this.selectedShippingId = undefined;
            this.clearSelection();
          }
        });
      });
    });
  }

  public openShipping(shipping: ShippingDto): void {
    this.selectedShippingId = shipping.shippingId!;
    this.clearSelection();
    this.formGroup.reset({ search: '' });
    this.search = '';
  }

  public closeShipping(): void {
    this.selectedShippingId = undefined;
    this.clearSelection();
    this.formGroup.reset({ search: '' });
    this.search = '';
  }

  @HostListener('window:keydown.enter', ['$event'])
  onKeyPress(evt: KeyboardEvent) {
    if (
      evt.key === 'Enter' &&
      this.hasDeliveredPermission &&
      this.selectedShippingId !== undefined &&
      this.formGroup.valid &&
      this.user === undefined &&
      this.creating === undefined
    ) {
      evt.preventDefault();
      this.validateRegisterNumber();
    }
  }

  clearSelection() {
    this.user = undefined;
    this.toDeliver = [];
    this.toDeliverMap = {};
    this.searchDone = false;
    this.isValid = false;
    this.hasAlreadyDelivered = false;
  }

  validateRegisterNumber() {
    if (
      !this.hasDeliveredPermission ||
      this.selectedShippingId === undefined
    ) {
      return;
    }

    this.clearSelection();

    const registerNumber = String(
      this.formGroup.get('search')?.value ?? '',
    ).trim();

    this.search = registerNumber;

    if (!registerNumber) {
      return;
    }

    const map = this.usersMap.find(
      (elem) =>
        String(elem.user.registerNumber ?? '').trim() === registerNumber,
    );

    if (map !== undefined) {
      this.toDeliver = Object.keys(map.shippingsMap)
        .map((elem) => {
          return map.shippingsMap[Number(elem)];
        })
        .filter((elem) => {
          return (
            elem.shippingId === this.selectedShippingId &&
            !elem.delivered
          );
        });

      this.toDeliver.forEach((elem) => {
        this.toDeliverMap[elem.shippingId] = false;
      });
    }

    if (map?.user !== undefined) {
      if (this.toDeliver.length === 0) {
        this.hasAlreadyDelivered = true;
      } else {
        this.isValid = true;
        this.user = map.user;
      }
    } else {
      this.isValid = false;
    }

    this.searchDone = true;
  }

  removeShipping(shipping: ShippingDto) {
    if (!this.hasDeletePermission) {
      return;
    }

    this.shippingService.removeShipping([shipping]).subscribe(() => {
      this.loadAll();
    });
  }

  createNewShipping() {
    if (!this.hasCreatePermission) {
      return;
    }

    this.creating = {
      shipping: {
        shipping: '',
        createdDate: new Date(),
        validFromDate: new Date(),
        validToDate: new Date(),
      },
      userIds: [],
    };
  }

  confirmCreate() {
    if (!this.hasCreatePermission) {
      return;
    }

    if (this.creating !== undefined) {
      this.shippingService.createShipping(this.creating).subscribe(() => {
        this.creating = undefined;
        this.loadAll();
      });
    }
  }

  assignUsers() {
    if (!this.hasCreatePermission || this.creating === undefined) {
      return;
    }

    this.accountService.getAuthList().subscribe((allUsers) => {
      const dialogRef: DynamicDialogRef = this.dialogService.open(
        AuthorizedUsersDialogComponent,
        {
          maximizable: false,
          draggable: false,
          height: '100%',
          width: '100%',
          closable: true,
          closeOnEscape: false,
          data: {
            selectedUsers: [],
            allUsers,
            allowSimplifiedView: true,
          },
        },
      );

      dialogRef.onClose.subscribe((authorized: any[]) => {
        if (!authorized || this.creating === undefined) {
          return;
        }

        this.creating.userIds = authorized.map(
          (elem) => elem.subscriber.subscriberId,
        );
      });
    });
  }

  setAsDelivered() {
    if (!this.hasDeliveredPermission || this.user === undefined) {
      return;
    }

    this.shippingService
      .setAsDelivered({
        user: `${this.accountService.user?.role} ${this.accountService.user?.name} ${this.accountService.user?.lastName}`,
        shippingIds: Object.keys(this.toDeliverMap)
          .filter((elem) => this.toDeliverMap[Number(elem)])
          .map((elem) => parseInt(elem, 10)),
        userId: this.user.subscriberId!,
      })
      .subscribe(() => {
        this.loadAll();
        this.clearSelection();
      });
  }
}
